import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { getPaymentRequestActivity } from "redux/erp/paymentRequestSlice";

// What kind of step each recorded action is — colour and icon on the timeline.
const KINDS = [
  { key: "signature", match: /certificate|signature/, icon: "bi-pen", dot: "tw-bg-[#ee46bc]", label: "Digital signature" },
  { key: "bank", match: /bank|hsbc|payment_files|export_payment/, icon: "bi-bank2", dot: "tw-bg-[#7a5af8]", label: "Bank" },
  { key: "erp", match: /erp|journal|reversal|po$|link_po/, icon: "bi-journal-text", dot: "tw-bg-[#2e90fa]", label: "ERP" },
  { key: "stop", match: /reject|cancel|failed/, icon: "bi-x-circle", dot: "tw-bg-[#f04438]", label: "Stopped" },
  { key: "approve", match: /approve|submit|sign|final_signer/, icon: "bi-check2-circle", dot: "tw-bg-[#12b76a]", label: "Approval" },
];
const kindOf = (action = "") => {
  const name = String(action);
  if (/failed/.test(name) && !/certificate/.test(name)) return KINDS.find((k) => k.key === "stop");
  return KINDS.find((k) => k.match.test(name)) || { key: "other", icon: "bi-dot", dot: "tw-bg-geist-fg-3", label: "Other" };
};
const FILTERS = [
  { key: "all", label: "All" }, { key: "approve", label: "Approvals" }, { key: "signature", label: "Digital signature" },
  { key: "erp", label: "ERP" }, { key: "bank", label: "Bank / HSBC" },
];
const SHOWN = 8;
const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Everything recorded on the request, newest first — submit, approvals, ERP
 * journals, HSBC file, bank release — so anyone following the payment can see
 * who did what and when.
 */
const ActivityTimeline = ({ paymentRequest: pr }) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(false);
  // Mặc định thu gọn: timeline dài làm rối trang chi tiết — ai cần mới mở ra.
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await dispatch(getPaymentRequestActivity(pr.id)).unwrap();
      setRows(res?.data || []);
    } catch {
      setRows([]);
    }
  }, [dispatch, pr.id]);

  useEffect(() => { load(); }, [load, pr.updated_at, pr.status?.id, pr.current_step, pr.hsbc_package?.status]);

  const visible = (rows || []).filter((r) => filter === "all" || kindOf(r.action).key === filter);
  const shown = expanded ? visible : visible.slice(0, SHOWN);

  const latest = rows?.[0];

  return (
    <section aria-label="Activity" className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-px-5 tw-py-4">
      <header className={`tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 ${open ? "tw-mb-3" : ""}`}>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={`pr-activity-${pr.id}`}
          className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left">
          <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"} tw-text-[11px] tw-text-geist-fg-3`} aria-hidden="true" />
          <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-geist-fg">Activity</h3>
          {rows?.length > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-geist-100 tw-text-[10px] tw-font-semibold tw-text-geist-fg-2 tw-tabular-nums">
              {rows.length}
            </span>
          )}
          {/* Thu gọn vẫn cho thấy việc mới nhất — đủ để biết có gì vừa xảy ra mà không phải mở. */}
          {!open && latest && (
            <span className="tw-min-w-0 tw-truncate tw-text-[12px] tw-text-geist-fg-3">
              Latest: {latest.comment || latest.action} · {when(latest.created_at)}
            </span>
          )}
        </button>
        {open && (
        <div className="tw-flex tw-items-center tw-gap-1" role="tablist" aria-label="Filter activity">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} onClick={() => setFilter(f.key)}
              className={`tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-px-2.5 tw-py-0.5 tw-text-[12px] ${
                filter === f.key ? "tw-border-geist-fg tw-bg-geist-fg tw-text-white" : "tw-border-geist-border tw-bg-transparent tw-text-geist-fg-2 hover:tw-bg-geist-100"}`}>
              {f.label}
            </button>
          ))}
          <button type="button" onClick={load} aria-label="Reload activity"
            className="tw-ml-1 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-geist-fg-3 hover:tw-text-geist-fg">
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
          </button>
        </div>
        )}
      </header>

      {open && (
      <div id={`pr-activity-${pr.id}`}>
      {rows === null ? (
        <p className="tw-m-0 tw-text-[13px] tw-text-geist-fg-3">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="tw-m-0 tw-text-[13px] tw-text-geist-fg-3">
          {filter === "all" ? "Nothing recorded yet." : `No ${FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} step recorded yet.`}
        </p>
      ) : (
        <ol className="tw-m-0 tw-p-0 tw-list-none">
          {shown.map((r, i) => {
            const kind = kindOf(r.action);
            return (
              <li key={r.id} data-kind={kind.key} className="tw-relative tw-flex tw-gap-3 tw-pb-3">
                {i < shown.length - 1 && <span aria-hidden="true" className="tw-absolute tw-left-[5px] tw-top-3 tw-bottom-0 tw-w-px tw-bg-geist-border" />}
                <span aria-hidden="true" className={`tw-relative tw-mt-1.5 tw-h-[11px] tw-w-[11px] tw-flex-shrink-0 tw-rounded-full ${kind.dot}`} />
                <div className="tw-min-w-0 tw-text-[13px]">
                  <p className="tw-m-0 tw-text-geist-fg tw-break-words">{r.comment || r.action}</p>
                  <p className="tw-m-0 tw-text-[12px] tw-text-geist-fg-3">
                    <i className={`bi ${kind.icon} tw-mr-1`} aria-hidden="true" />
                    {when(r.created_at)}{r.user_name ? ` · ${r.user_name}` : ""} · <code>{r.action}</code>
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {visible.length > SHOWN && (
        <button type="button" onClick={() => setExpanded((v) => !v)}
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-semibold tw-text-geist-accent">
          {expanded ? "Show less" : `Show all ${visible.length}`}
        </button>
      )}
      </div>
      )}
    </section>
  );
};

ActivityTimeline.propTypes = { paymentRequest: PropTypes.object.isRequired };

export default ActivityTimeline;
